import { useEffect, useMemo, useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { TbCopy } from "react-icons/tb";
import { useToast } from "../../../../Hooks/toast";
import { useContaInternacionalBalances } from "../../../../Hooks/useContaInternacionalBalances";
import { useContaInternacionalCurrencies } from "../../../../Hooks/useContaInternacionalCurrencies";
import {
  getCurrencyBankingDetails,
  getCurrencyRails,
} from "../../../../Services/ibAccountCurrenciesApi";
import { CurrencySelect } from "./CurrencySelect";
import {
  ACCOUNT_DATA_FIELD_LABELS,
  DEFAULT_CURRENCY_ID,
  getCurrency,
} from "../constants";

const COPYABLE_FIELDS = new Set([
  "bankName",
  "accountNumber",
  "iban",
  "swiftBic",
  "routingNumber",
  "sortCode",
  "branch",
  "pixKey",
  "beneficiary",
  "referenceInstructions",
]);

const DetailField = ({ fieldKey, value, onCopy }) => {
  if (!value) return null;

  const label = ACCOUNT_DATA_FIELD_LABELS[fieldKey];
  const canCopy = COPYABLE_FIELDS.has(fieldKey);

  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      {canCopy ? (
        <div className="mt-1 flex items-start gap-2">
          <code className="min-w-0 flex-1 break-all rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-brand-dark">
            {value}
          </code>
          <button
            type="button"
            className="btn-outline-icon !shrink-0"
            onClick={() => onCopy(value, label)}
            title={`Copiar ${label.toLowerCase()}`}
            aria-label={`Copiar ${label.toLowerCase()}`}
          >
            <TbCopy className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <p className="mt-1 text-sm font-semibold text-brand-dark">{value}</p>
      )}
    </div>
  );
};

const RailDetails = ({ details, onCopy }) => {
  if (!details) {
    return (
      <p className="text-sm text-gray-500">
        Dados bancários indisponíveis para este método no momento.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {Object.entries(details).map(([fieldKey, value]) => (
        <DetailField key={fieldKey} fieldKey={fieldKey} value={value} onCopy={onCopy} />
      ))}
    </div>
  );
};

export const ContaInternacionalDepositarContent = ({
  isActive = true,
  currencyId,
}) => {
  const toast = useToast();
  const { allCurrencies } = useContaInternacionalBalances();
  const { currencies: currencyOpenings, loading: currenciesLoading } =
    useContaInternacionalCurrencies();

  const currencyOptions = useMemo(() => {
    if (allCurrencies?.length) return allCurrencies;
    return currencyOpenings.map((row) => ({
      id: row.id,
      label: row.label,
      code: row.code,
      balance: 0,
    }));
  }, [allCurrencies, currencyOpenings]);

  const defaultId = currencyOptions[0]?.id ?? DEFAULT_CURRENCY_ID;
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(currencyId ?? defaultId);
  const effectiveCurrencyId = currencyId ?? selectedCurrencyId;
  const currency =
    currencyOptions.find((row) => row.id === effectiveCurrencyId) ||
    getCurrency(effectiveCurrencyId);

  const liveRails = getCurrencyRails(currencyOpenings, effectiveCurrencyId);
  const rails = liveRails;

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!isActive) return;
    setSelectedCurrencyId(currencyId ?? defaultId);
    setActiveTab(0);
  }, [isActive, currencyId, defaultId]);

  useEffect(() => {
    setActiveTab(0);
  }, [effectiveCurrencyId]);

  const handleCopy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.addToast({
        type: "success",
        title: "Copiado",
        description: `${label} copiado para a área de transferência.`,
      });
    } catch {
      toast.addToast({
        type: "error",
        title: "Erro ao copiar",
        description: "Não foi possível copiar o valor.",
      });
    }
  };

  if (!currency) return null;

  const activeRail = rails[activeTab];
  const details = activeRail
    ? getCurrencyBankingDetails(currencyOpenings, effectiveCurrencyId, activeRail.id)
    : null;

  return (
    <div>
      {!currencyId && (
        <CurrencySelect
          id="deposit-currency"
          label="Moeda"
          value={selectedCurrencyId}
          onChange={setSelectedCurrencyId}
          options={currencyOptions}
        />
      )}

      <p className={`text-sm text-gray-500 ${currencyId ? "" : "mt-4"}`}>
        Informações bancárias para depositar {currency.label} ({currency.code}).
      </p>

      {currenciesLoading ? (
        <p className="mt-4 text-sm text-gray-400">Carregando dados bancários…</p>
      ) : (
        <>
          {rails.length > 1 && (
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              className="!mt-4 !min-h-0"
              sx={{
                minHeight: 40,
                "& .MuiTab-root": {
                  minHeight: 40,
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  textTransform: "none",
                },
              }}
            >
              {rails.map((rail) => (
                <Tab key={rail.id} label={rail.label} />
              ))}
            </Tabs>
          )}

          {rails.length === 1 && (
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {rails[0].label}
            </p>
          )}

          <div className="mt-4 rounded-xl border border-gray-100 bg-white p-4">
            {activeRail ? (
              <RailDetails details={details} onCopy={handleCopy} />
            ) : (
              <p className="text-sm text-gray-500">
                Nenhum método de recebimento disponível para esta moeda.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
